import { Templates, WixFlow } from '@wix/funnel-builder-lizard'
import React, { ComponentProps, FC, PropsWithChildren, useEffect, useMemo } from 'react'

import { HttpFunnelBuilderService } from '../../api/FunnelBuilderService/FunnelBuilderService'
import { FlowLoggerProvider, PageLoggerProvider } from '../../bi/mfi'
import { useDebugTranslationKeys } from '../../hooks/useDebugTranslationKeys'
import { useFlowContext } from '../../hooks/useFlowContext'
import { useForceLanguage } from '../../hooks/useForceLanguage'
import { useForcePage } from '../../hooks/useForcePage'
import { usePageId } from '../../hooks/usePageId'
import { enrichSentry } from '../../monitoring/enrichSentry'
import { useErrorNotification } from '../ErrorNotification/ErrorNotificationContext'
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader'
import { AppTemplates } from './AppTemplates'
import { hasTemplatesFallback, TemplatesFallback } from './TemplatesFallback'

const WixFlowWithChildren = WixFlow as FC<PropsWithChildren<ComponentProps<typeof WixFlow>>>
const TemplatesWithChildren = Templates as FC<PropsWithChildren<ComponentProps<typeof Templates>>>

export const AppInStorageProvider: FC = () => {
  const [flowContext, setFlowContext] = useFlowContext()
  const pageId = usePageId()
  const forcePage = useForcePage()
  const forceLanguage = useForceLanguage()
  const debugTranslationKeys = useDebugTranslationKeys()

  const { showError, hideError } = useErrorNotification()

  const flowId = flowContext?.historyEntries[0]?.flowId ?? 'preview'
  const funnelBuilderService = useMemo(() => new HttpFunnelBuilderService(flowId, showError), [flowId, showError])

  const flowBiSlug = flowContext?.metadata?.biSlug || 'unknown'
  const metaSiteId = flowContext?.metadata?.metaSiteId

  useEffect(() => {
    enrichSentry(flowContext)
  }, [flowContext])

  // Successful navigation invalidates a previously shown request-failure notification
  useEffect(() => {
    hideError()
  }, [flowContext, hideError])

  if (!flowContext && !pageId) {
    window.location.replace('https://www.wix.com/website/templates')
    return null
  }

  return (
    <FlowLoggerProvider flowBiSlug={flowBiSlug} metaSiteId={metaSiteId} referralName={flowContext?.metadata?.ref}>
      <WixFlowWithChildren
        flowContext={flowContext}
        updateFlowContext={setFlowContext}
        service={funnelBuilderService}
        fallback={hasTemplatesFallback(flowId) ? () => <TemplatesFallback flowId={flowId} /> : undefined}
      >
        <TemplatesWithChildren
          Loader={SkeletonLoader}
          pageId={pageId}
          forcePage={forcePage}
          forceLanguage={forceLanguage}
          debugTranslationKeys={debugTranslationKeys}
        >
          <PageLoggerProvider>
            <AppTemplates />
          </PageLoggerProvider>
        </TemplatesWithChildren>
      </WixFlowWithChildren>
    </FlowLoggerProvider>
  )
}
